import { useMutation } from '@tanstack/react-query';
import classNames from 'classnames';
import { Check } from 'lucide-react';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

import { CreditTopupAutoReload } from './CreditTopupAutoReload';
import {
  CREDIT_TOPUP_RETURN_STATUS_QUERY_PARAM,
  getCreditTopupEntrypoint,
  saveCreditTopupCheckoutAttribution,
} from './creditTopupAnalytics';
import { QueryKey } from '@/Config/api/queryKey';
import {
  CreditTopupCheckoutInput,
  CreditTopupCheckoutUrl,
} from '@/Domain/api/Subscription/dto/CreditTopup.dto';
import { Button } from '@/Domain/components/Shared/Button/Button';
import { getUsageBarColor } from '@/Domain/components/TierPlanCard/usageColor';
import { DiscordIcon } from '@/Domain/components/icons/Discord.icon';
import { PostHogEventEnum } from '@/Domain/enum/PostHogEvent.enum';
import { usePostHogAnalytics } from '@/Domain/hooks/App/usePostHog/usePostHogAnalytics.hook';
import { useCreditTopupOffer } from '@/Domain/hooks/Subscription/useCreditTopupOffer.hook';

const BENEFIT_KEYS = ['never_expires', 'after_allowance'] as const;
const AUTO_RELOAD_HINT_ID = 'credit-topup-auto-reload-hint';

export const DiscordCreditTopupPanel = () => {
  const { t } = useTranslation('subscription_page');
  const { capture } = usePostHogAnalytics();
  const { offer, isEligible } = useCreditTopupOffer();
  const [searchParams] = useSearchParams();
  const entrypoint = getCreditTopupEntrypoint(searchParams);
  const checkoutReturnOnMount = useRef(searchParams.has(CREDIT_TOPUP_RETURN_STATUS_QUERY_PARAM));
  const panelViewTracked = useRef(false);

  useEffect(() => {
    if (
      checkoutReturnOnMount.current ||
      panelViewTracked.current ||
      !offer ||
      (!isEligible && !offer.auto_reload.enabled)
    ) {
      return;
    }
    panelViewTracked.current = true;
    capture(PostHogEventEnum.CreditTopupPanelViewed, {
      entrypoint,
      credits: offer.credits,
      price_usd: offer.price_usd,
      topup_credits: offer.topup_credits,
      credits_purchased_total: offer.credits_purchased_total,
      auto_reload_available: offer.auto_reload.available,
      auto_reload_enabled: offer.auto_reload.enabled,
    });
  }, [capture, entrypoint, isEligible, offer]);

  const {
    mutate: startCheckout,
    isPending,
    variables: pendingCheckout,
  } = useMutation<CreditTopupCheckoutUrl, unknown, CreditTopupCheckoutInput>({
    mutationKey: [QueryKey.subscription.creditTopupCheckout],
    onSuccess: ({ url }, input) => {
      const hasBalance = (offer?.topup_credits ?? 0) > 0;
      saveCreditTopupCheckoutAttribution({
        flow: 'purchase',
        entrypoint: input.entrypoint,
        auto_reload: input.auto_reload,
        has_balance: hasBalance,
      });
      capture(PostHogEventEnum.CreditTopupCheckoutStarted, {
        credits: offer?.credits,
        price_usd: offer?.price_usd,
        auto_reload: input.auto_reload,
        purchase_mode: input.auto_reload ? 'auto_reload' : 'one_time',
        entrypoint: input.entrypoint,
        has_balance: hasBalance,
      });
      window.location.href = url;
    },
    onError: (_error, input) => {
      capture(PostHogEventEnum.CreditTopupCheckoutCreationFailed, {
        credits: offer?.credits,
        price_usd: offer?.price_usd,
        auto_reload: input.auto_reload,
        purchase_mode: input.auto_reload ? 'auto_reload' : 'one_time',
        entrypoint: input.entrypoint,
        has_balance: (offer?.topup_credits ?? 0) > 0,
      });
      toast.error(t('topup_panel.checkout_error'));
    },
  });

  if (!offer) return null;

  if (!isEligible) {
    if (!offer.auto_reload.enabled) return null;
    return (
      <section
        className="rounded-2xl border border-border bg-card p-6"
        data-testid="credit-topup-auto-reload-paused"
      >
        <div className="max-w-xl">
          <h2 className="text-base font-semibold text-foreground">
            {t('topup_panel.auto_reload.unavailable_title')}
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">
            {t('topup_panel.auto_reload.unavailable_description')}
          </p>
          <div className="mt-4">
            <CreditTopupAutoReload
              offer={offer}
              offerCardSetup={false}
              canEnable={false}
              entrypoint={entrypoint}
            />
          </div>
        </div>
      </section>
    );
  }

  const credits = offer.credits.toLocaleString();
  const balance = offer.topup_credits;
  const hasBalance = balance > 0;
  // The immutable purchase ledger is the source for lifetime purchased credits.
  const purchasedTotal = Math.max(offer.credits_purchased_total, balance);
  const used = Math.max(0, purchasedTotal - balance);
  const percentUsed = purchasedTotal > 0 ? Math.min(100, (used / purchasedTotal) * 100) : 0;

  const buyLabelKey = hasBalance ? 'topup_panel.buy_more' : 'topup_panel.buy';
  const showInitialPurchaseChoices = !hasBalance && !offer.auto_reload.available;

  return (
    <section
      data-brand="discord"
      data-testid="credit-topup-panel"
      className="rounded-2xl border border-border bg-card p-6 sm:p-8"
    >
      <div className="flex flex-col gap-8 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex min-w-0 flex-col gap-4">
          <div className="flex flex-wrap items-center gap-3">
            <span
              className="inline-flex size-10 shrink-0 items-center justify-center rounded-xl bg-[var(--qc-discord-blurple)] text-white"
              data-testid="credit-topup-discord-mark"
            >
              <DiscordIcon className="size-5" style={{ fill: 'currentColor' }} aria-hidden="true" />
            </span>
            <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              {t('topup_panel.badge')}
            </span>
          </div>

          {hasBalance ? (
            <div className="flex flex-col gap-2" data-testid="credit-topup-balance">
              <h2 className="text-2xl font-semibold tracking-tight text-foreground">
                {t('topup_panel.balance_headline', { credits: balance.toLocaleString() })}
              </h2>
              <div className="h-2 w-full max-w-sm overflow-hidden rounded-full bg-muted-foreground/15">
                <div
                  className={classNames(
                    'h-full rounded-full transition-all duration-500 ease-out',
                    getUsageBarColor(percentUsed),
                  )}
                  style={{ width: `${percentUsed}%` }}
                />
              </div>
              <p className="text-sm text-muted-foreground" data-testid="credit-topup-used">
                {t('topup_panel.used_of_total', {
                  used: used.toLocaleString(),
                  total: purchasedTotal.toLocaleString(),
                })}
              </p>
            </div>
          ) : (
            <div>
              <h2 className="text-2xl font-semibold tracking-tight text-foreground">
                {t('topup_panel.title', { credits, price: offer.price_usd })}
              </h2>
              <p className="mt-1 text-sm text-muted-foreground">{t('topup_panel.unit_price')}</p>
            </div>
          )}

          <ul className="flex flex-col gap-2 text-sm text-muted-foreground">
            {BENEFIT_KEYS.map((key) => (
              <li key={key} className="flex items-start gap-2">
                <Check
                  className="mt-0.5 size-4 shrink-0 text-[var(--qc-discord-blurple)]"
                  aria-hidden="true"
                />
                <span>{t(`topup_panel.benefits.${key}`)}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="flex w-full shrink-0 flex-col gap-4 rounded-xl bg-muted/50 p-5 lg:w-80">
          {showInitialPurchaseChoices ? (
            <div className="flex flex-col gap-2">
              <Button
                variant="secondary"
                className="h-auto min-h-10 w-full whitespace-normal py-2.5 text-center leading-snug"
                disabled={isPending}
                loading={isPending && pendingCheckout?.auto_reload === false}
                onClick={() => startCheckout({ auto_reload: false, entrypoint })}
                data-testid="credit-topup-buy-once-button"
              >
                {t('topup_panel.buy', { credits, price: offer.price_usd })}
              </Button>
              <div className="flex flex-col gap-1.5">
                <Button
                  variant="custom"
                  className="h-auto min-h-10 w-full whitespace-normal border-0 bg-[var(--qc-discord-blurple)] py-2.5 text-center leading-snug text-white hover:bg-[var(--qc-discord-blurple-hover)]"
                  disabled={isPending}
                  loading={isPending && pendingCheckout?.auto_reload === true}
                  onClick={() => startCheckout({ auto_reload: true, entrypoint })}
                  aria-describedby={AUTO_RELOAD_HINT_ID}
                  data-testid="credit-topup-buy-auto-reload-button"
                >
                  {t('topup_panel.buy_with_auto_reload', {
                    credits,
                    price: offer.price_usd,
                  })}
                </Button>
                <p
                  id={AUTO_RELOAD_HINT_ID}
                  className="text-xs text-foreground-subtle"
                  data-testid="credit-topup-auto-reload-hint"
                >
                  {t('topup_panel.auto_reload.hint_at_checkout', {
                    credits,
                    price: offer.price_usd,
                    threshold: offer.auto_reload.threshold_credits.toLocaleString(),
                  })}
                </p>
              </div>
            </div>
          ) : (
            <>
              <Button
                variant="custom"
                className="h-auto min-h-10 w-full whitespace-normal border-0 bg-[var(--qc-discord-blurple)] py-2.5 text-center leading-snug text-white hover:bg-[var(--qc-discord-blurple-hover)]"
                disabled={isPending}
                loading={isPending}
                onClick={() => startCheckout({ auto_reload: false, entrypoint })}
                data-testid="credit-topup-buy-once-button"
              >
                {t(buyLabelKey, { credits, price: offer.price_usd })}
              </Button>

              <CreditTopupAutoReload
                offer={offer}
                offerCardSetup={hasBalance}
                entrypoint={entrypoint}
              />
            </>
          )}

          <p className="text-xs text-foreground-subtle">{t('topup_panel.no_expiry')}</p>
        </div>
      </div>
    </section>
  );
};
